// Copied from platform-app components/pos/layout/RailPreview.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useRef } from 'react';
import { BUTTON_REGISTRY, mergeButton, type ButtonOverride } from '../../../config/buttonRegistry';
import { resolveButtonLook, THEME_TILE } from '../../../config/buttonTheme';
import { getIcon } from '../../../utils/iconRegistry';
import { GripVertical, Menu, X, Palette } from 'lucide-react';
import { PREVIEW_PALETTE, type PreviewTheme } from './previewPalette';

export interface RailPreviewProps {
  pinnedIds: string[];
  customizations: Record<string, ButtonOverride>;
  onUnpin: (id: string) => void;
  onReorder: (fromId: string, toIndex: number) => void;
  onCustomize: (id: string) => void;
  theme?: PreviewTheme;
}

// Rail geometry (matches the approved Side Rail mockup): 68px tiles, 8px gap, 12px top padding.
const TILE = 68;
const GAP = 8;
const PAD_TOP = 12;
const PITCH = TILE + GAP;
/** Marker sits in the gap below tile n. */
const markerTop = (n: number) => PAD_TOP + n * PITCH - GAP / 2;

const CAPACITY_MARKERS: { after: number; label: string; tone: 'amber' | 'green' }[] = [
  { after: 5, label: '1280×800 · 5 fit, rest scroll', tone: 'amber' },
  { after: 9, label: '1920×1080 · 9 fit', tone: 'green' },
];

export default function RailPreview({ pinnedIds, customizations, onUnpin, onReorder, onCustomize, theme = 'light' }: RailPreviewProps) {
  const dragId = useRef<string | null>(null);
  const p = PREVIEW_PALETTE[theme];
  const t = THEME_TILE[theme];
  // Tall enough that both capacity markers stay visible.
  const minRowHeight = markerTop(10) + GAP;

  return (
    <div data-testid="rail-preview" className="overflow-hidden rounded-[14px] border border-stone-300 flex flex-col" style={{ background: '#ffffff' }}>
      {/* Header silhouette */}
      <div className="flex h-[26px] items-center gap-1.5 px-2.5" style={{ background: p.head, borderBottom: `1px solid ${p.railBor}` }}>
        <i className="block h-3.5 w-3.5 rounded" style={{ background: '#DC143C' }} />
        <i className="block h-[7px] w-[46px] rounded opacity-40" style={{ background: p.mu }} />
      </div>

      <div className="relative flex" style={{ minHeight: minRowHeight }}>
        {/* The rail */}
        <div
          className="flex w-[84px] flex-none flex-col items-center"
          style={{ background: p.railBg, borderRight: `1px solid ${p.railBor}`, gap: GAP, paddingTop: PAD_TOP, paddingBottom: 10 }}
        >
          {pinnedIds.map((id, index) => {
            const reg = BUTTON_REGISTRY[id];
            const merged = mergeButton(id, customizations[id]);
            if (!reg || !merged) return null;
            const Icon = getIcon(merged.icon);
            const look = resolveButtonLook(id, customizations[id], theme);
            return (
              <div
                key={id}
                draggable
                onDragStart={() => { dragId.current = id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (dragId.current && dragId.current !== id) onReorder(dragId.current, index); dragId.current = null; }}
                onDragEnd={() => { dragId.current = null; }}
                className="group relative flex flex-none cursor-grab flex-col items-center justify-center gap-1.5 rounded-xl text-center text-[10.5px] font-medium leading-[1.1] hover:outline hover:outline-2 hover:outline-offset-2 hover:outline-accent-500"
                style={{ ...look.style, width: TILE, height: TILE }}
                title="Drag to reorder"
              >
                <GripVertical size={12} className="absolute left-1 top-1" style={{ color: look.fg, opacity: 0.45 }} />
                {Icon && <Icon size={22} color={look.fg} />}
                <span className="line-clamp-2 max-w-[62px] break-words px-0.5">{merged.label}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onCustomize(id); }}
                  className="absolute bottom-[3px] right-[3px] flex h-[18px] w-[18px] items-center justify-center rounded-[5px] opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100"
                  title="Customize"
                >
                  <Palette size={11} color={look.fg} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onUnpin(id); }}
                  className="absolute -right-[7px] -top-[7px] flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 shadow"
                  title="Unpin"
                >
                  <X size={11} strokeWidth={3.5} color="#ffffff" />
                </button>
              </div>
            );
          })}
          <div
            className="flex flex-none flex-col items-center justify-center gap-1.5 rounded-xl text-[10.5px] font-medium"
            style={{ width: TILE, height: TILE, background: p.moreBg, border: `1px solid ${p.moreBg}`, color: p.moreTx }}
          >
            <Menu size={22} /> More
          </div>
        </div>

        {/* Cart placeholder */}
        <div className="flex flex-1 flex-col gap-[9px] p-3" style={{ background: '#ffffff' }}>
          {pinnedIds.length === 0 ? (
            <span className="py-2 text-xs italic text-stone-500">No buttons pinned — tap tiles in the More launcher to add them to the rail.</span>
          ) : (
            <>
              <i className="block h-[30px] rounded-lg" style={{ background: '#f4f4f5' }} />
              {Array.from({ length: 6 }).map((_, i) => (
                <i key={i} className="block h-10 rounded-lg" style={{ background: '#f1f1f2' }} />
              ))}
            </>
          )}
        </div>

        {/* Keypad placeholder */}
        <div className="grid w-[118px] flex-none grid-cols-3 content-start gap-[5px] p-2.5" style={{ background: p.railBg, gridAutoRows: 34 }}>
          {Array.from({ length: 15 }).map((_, i) => (
            <i key={i} className="block rounded-md" style={{ background: t.bg, border: `1px solid ${t.bor}` }} />
          ))}
        </div>

        {/* Capacity markers */}
        {CAPACITY_MARKERS.map((m) => (
          <div
            key={m.after}
            aria-hidden
            className={`pointer-events-none absolute left-0 right-0 z-[2] h-0 border-t-2 border-dashed ${m.tone === 'amber' ? 'border-amber-500' : 'border-accent-500'}`}
            style={{ top: markerTop(m.after) }}
          >
            <span
              className={`absolute right-2 -top-3 whitespace-nowrap rounded-md border px-[7px] py-[5px] text-[11px] font-semibold leading-none ${
                m.tone === 'amber' ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-accent-300 bg-accent-50 text-accent-700'
              }`}
            >
              {m.label}
            </span>
          </div>
        ))}
      </div>

      {/* Summary bar silhouette */}
      <div className="flex h-12 items-center justify-end px-2.5" style={{ background: p.head, borderTop: `1px solid ${p.railBor}` }}>
        <i className="block h-8 w-[118px] rounded-lg" style={{ background: '#DC143C' }} />
      </div>
    </div>
  );
}
